import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { StatCard } from '@/components/StatCard'
import { ErrorState, LoadingSkeleton } from '@/components/States'
import { StatusBadge } from '@/components/StatusBadge'
import { ActivityTimeline } from '@/components/ActivityTimeline'
import { DocumentDetailDrawer } from '@/components/DocumentDetailDrawer'
import { IconOrganizations, IconUsers, IconDocuments, IconClock, IconActivity } from '@/components/Icons'
import { useOrganization, useOrganizationUsers, useDocuments, useSignatures, useActivity } from '@/hooks/useRootQueries'
import { updateOrgStatus, downloadDocumentFile } from '@/api/root'
import { toast } from '@/components/Toast'
import { exactTimestamp, relativeTime } from '@/utils/time'
import type { AuditEvent } from '@/types/api'

type Tab = 'overview' | 'users' | 'documents' | 'signatures' | 'activity'

export function OrganizationDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('overview')
  const [updating, setUpdating] = useState(false)
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null)
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null)
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null)

  const org = useOrganization(id)
  const users = useOrganizationUsers(id)
  const documents = useDocuments({ organization: id, pageSize: 50 })
  // Query signatures by organization ID so all signers for this org are loaded properly
  const signatures = useSignatures({ organization: id, pageSize: 50 })
  // Dedicated organization audit trail
  const orgActivity = useActivity({ organization: id, pageSize: 50 })

  if (org.isLoading) {
    return <LoadingSkeleton rows={6} />
  }
  if (org.isError || !org.data) {
    return <ErrorState message="Could not load this organization." onRetry={() => org.refetch()} />
  }

  const { organization, userCount, documentCount, recentActivity } = org.data
  const isSuspended = organization.status === 'suspended'

  async function handleToggleStatus() {
    if (!id) return
    const nextStatus = isSuspended ? 'active' : 'suspended'
    const actionLabel = isSuspended ? 'reactivate' : 'suspend'
    if (!window.confirm(`Are you sure you want to ${actionLabel} ${organization.name}?`)) return

    try {
      setUpdating(true)
      await updateOrgStatus(id, nextStatus)
      toast.success(`Organization status updated to ${nextStatus}`)
      org.refetch()
    } catch {
      toast.error('Failed to update organization status')
    } finally {
      setUpdating(false)
    }
  }

  async function handleDownloadDoc(e: React.MouseEvent, docId: string, docTitle?: string) {
    e.stopPropagation()
    try {
      setDownloadingDocId(docId)
      toast.info('Initiating document download...')
      await downloadDocumentFile(docId, docTitle ? `${docTitle}.pdf` : undefined)
      toast.success('Document downloaded successfully')
    } catch (err: any) {
      toast.error(err?.message || 'Failed to download document')
    } finally {
      setDownloadingDocId(null)
    }
  }

  return (
    <div className="space-y-5">
      <button
        onClick={() => navigate('/organizations')}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-sky-700 transition-colors"
      >
        <span>←</span>
        <span>Back to all organizations</span>
      </button>

      {/* Org Header Card */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3.5">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-sky-600 to-blue-700 text-white text-xl font-bold shadow-md">
            <IconOrganizations className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">{organization.name}</h1>
              <StatusBadge status={organization.status} />
            </div>
            <p className="font-mono text-xs text-slate-500 mt-0.5">Slug: {organization.slug} · Org ID: {organization.id}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleToggleStatus}
            disabled={updating}
            className={`rounded-lg px-4 py-2 font-mono text-xs font-bold transition-all shadow-xs ${
              isSuspended
                ? 'border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100'
                : 'border border-rose-300 bg-rose-50 text-rose-800 hover:bg-rose-100'
            }`}
          >
            {updating ? 'Updating…' : isSuspended ? 'Reactivate Organization' : 'Suspend Organization'}
          </button>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Registered Users" value={userCount} icon={<IconUsers className="h-4 w-4" />} tone="up" />
        <StatCard label="Total Documents" value={documentCount} icon={<IconDocuments className="h-4 w-4" />} tone="neutral" />
        <StatCard label="Created Date" value={new Date(organization.createdAt).toLocaleDateString()} icon={<IconClock className="h-4 w-4" />} />
        <StatCard label="Tenancy State" value={organization.status.toUpperCase()} icon={<IconActivity className="h-4 w-4" />} tone={isSuspended ? 'crit' : 'up'} />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {(['overview', 'users', 'documents', 'signatures', 'activity'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`border-b-2 px-4 py-2.5 text-xs font-bold capitalize transition-all ${
              tab === t ? 'border-sky-600 text-sky-800' : 'border-transparent text-slate-500 hover:text-slate-900'
            }`}
          >
            {t === 'signatures' ? 'Signers & Signatures' : t}
          </button>
        ))}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-xs">
        {tab === 'overview' && (
          <div>
            <h2 className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-700">Recent Organization Audit Events</h2>
            <ActivityTimeline
              events={recentActivity}
              onSelect={(e) => {
                setSelectedEvent(e)
                if (e.documentId || e.document_id) {
                  setSelectedDocId(e.documentId || e.document_id || null)
                }
              }}
            />
          </div>
        )}

        {tab === 'users' && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-3">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800">
                  Registered Organization Members
                </h2>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Internal staff and team members with active accounts on the platform.
                </p>
              </div>
              <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-md">
                {Array.isArray(users.data?.data) ? users.data.data.length : 0} Members
              </span>
            </div>

            {users.isLoading && <LoadingSkeleton rows={4} />}
            {users.isError && <ErrorState message="Could not load users for this organization." />}
            {users.data && Array.isArray(users.data.data) && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10.5px] font-bold uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                      <th className="px-3 py-2.5">User Identity / Email</th>
                      <th className="px-3 py-2.5">Role</th>
                      <th className="px-3 py-2.5">Status</th>
                      <th className="px-3 py-2.5">Documents</th>
                      <th className="px-3 py-2.5">Last Activity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {users.data.data.map((u) => (
                      <tr
                        key={u.id}
                        onClick={() => navigate(`/users/${u.id}`)}
                        className="hover:bg-sky-50/40 cursor-pointer transition-colors"
                      >
                        <td className="px-3 py-2.5">
                          <div className="font-bold text-slate-900">{u.name}</div>
                          <div className="font-mono text-[10.5px] text-slate-500 font-semibold select-all">{u.email}</div>
                        </td>
                        <td className="px-3 py-2.5 capitalize font-semibold text-slate-800">{u.role}</td>
                        <td className="px-3 py-2.5"><StatusBadge status={u.status} /></td>
                        <td className="px-3 py-2.5 font-mono text-slate-600">
                          {u.documentsCount ?? 0} docs
                        </td>
                        <td className="px-3 py-2.5 font-mono text-slate-500">
                          {u.lastActivityAt || u.lastLoginAt ? relativeTime(u.lastActivityAt || u.lastLoginAt!) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'documents' && (
          <div className="space-y-3">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800">Tenant Documents</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Click download to inspect signed cryptographic artifacts.</p>
              </div>
              <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-md">
                {Array.isArray(documents.data?.data) ? documents.data.data.length : 0} Documents
              </span>
            </div>
            {documents.isLoading && <LoadingSkeleton rows={4} />}
            {documents.isError && <ErrorState message="Could not load tenant documents." />}
            {documents.data && Array.isArray(documents.data.data) && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10.5px] font-bold uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                      <th className="px-3 py-2.5">Document Title</th>
                      <th className="px-3 py-2.5">Category</th>
                      <th className="px-3 py-2.5">Status</th>
                      <th className="px-3 py-2.5">Created</th>
                      <th className="px-3 py-2.5 text-right">Actions &amp; Download</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {documents.data.data.map((d) => (
                      <tr key={d.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2.5">
                          <div className="font-bold text-slate-900">{d.title}</div>
                          <div className="font-mono text-[10px] text-slate-400">ID: {d.id}</div>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-slate-600">{d.category || 'General'}</td>
                        <td className="px-3 py-2.5"><StatusBadge status={d.status} /></td>
                        <td className="px-3 py-2.5 font-mono text-slate-500">{relativeTime(d.createdAt)}</td>
                        <td className="px-3 py-2.5 text-right space-x-2">
                          <button
                            onClick={(e) => handleDownloadDoc(e, d.id, d.title)}
                            disabled={downloadingDocId === d.id}
                            className="inline-flex items-center gap-1 rounded-lg border border-emerald-300 bg-emerald-50 px-2.5 py-1 font-mono text-[11px] font-bold text-emerald-800 hover:bg-emerald-100 hover:border-emerald-400 shadow-2xs transition-all disabled:opacity-50"
                            title="Download Signed Document PDF"
                          >
                            <span>📥</span>
                            <span>{downloadingDocId === d.id ? 'Downloading…' : 'Download PDF'}</span>
                          </button>
                          <button
                            onClick={() => setSelectedDocId(d.id)}
                            className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-sky-300 hover:text-sky-700 shadow-2xs"
                          >
                            Details →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'signatures' && (
          <div className="space-y-3">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800">Organization Signers &amp; Signature Activity</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">All recipients assigned or completed signatures across this organization's documents.</p>
              </div>
              <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-md">
                {Array.isArray(signatures.data?.data) ? signatures.data.data.length : 0} Signers
              </span>
            </div>
            {signatures.isLoading && <LoadingSkeleton rows={4} />}
            {signatures.isError && <ErrorState message="Could not load tenant signatures." />}
            {!signatures.isLoading && !signatures.isError && (!Array.isArray(signatures.data?.data) || signatures.data.data.length === 0) && (
              <div className="py-8 text-center text-xs font-mono text-slate-500">
                No signers recorded for this organization's documents yet.
              </div>
            )}
            {!signatures.isLoading && !signatures.isError && Array.isArray(signatures.data?.data) && signatures.data.data.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-[10.5px] font-bold uppercase text-slate-500 border-b border-slate-200 bg-slate-50">
                      <th className="px-3 py-2.5">Signer Name / Email</th>
                      <th className="px-3 py-2.5">Document</th>
                      <th className="px-3 py-2.5">Signature Type</th>
                      <th className="px-3 py-2.5">Status</th>
                      <th className="px-3 py-2.5">Signed At</th>
                      <th className="px-3 py-2.5 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {signatures.data.data.map((s) => (
                      <tr key={s.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-3 py-2.5">
                          <div className="font-bold text-slate-900">{s.name}</div>
                          <div className="font-mono text-[10.5px] text-slate-500 select-all font-semibold">{s.email}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <button
                            onClick={() => setSelectedDocId(s.docId)}
                            className="font-semibold text-sky-700 hover:underline block truncate max-w-[200px]"
                            title={s.docTitle}
                          >
                            {s.docTitle || `Doc #${s.docId.slice(-6)}`}
                          </button>
                        </td>
                        <td className="px-3 py-2.5 font-mono text-slate-600 text-[10.5px]">
                          {s.signatureType || 'Electronic Sign'}
                        </td>
                        <td className="px-3 py-2.5"><StatusBadge status={s.status} /></td>
                        <td className="px-3 py-2.5 font-mono text-slate-500">
                          {s.signedAt ? exactTimestamp(s.signedAt) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            onClick={() => setSelectedDocId(s.docId)}
                            className="rounded border border-slate-200 bg-white px-2 py-1 text-[10.5px] font-semibold text-slate-700 hover:border-sky-300 hover:text-sky-700 shadow-2xs"
                          >
                            Details →
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'activity' && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-xs font-bold uppercase tracking-wider text-slate-800">Tenant Audit Trail</h2>
                <p className="text-[11px] text-slate-500 mt-0.5">Strictly isolated cryptographic and access events for this organization.</p>
              </div>
              <span className="font-mono text-xs font-bold text-slate-600 bg-slate-100 px-2.5 py-1 rounded-md">
                {(Array.isArray(orgActivity.data?.data) ? orgActivity.data.data : Array.isArray(recentActivity) ? recentActivity : []).length} Events
              </span>
            </div>
            <ActivityTimeline
              events={Array.isArray(orgActivity.data?.data) ? orgActivity.data.data : Array.isArray(recentActivity) ? recentActivity : []}
              onSelect={(e) => {
                setSelectedEvent(e)
                if (e.documentId || e.document_id) {
                  setSelectedDocId(e.documentId || e.document_id || null)
                }
              }}
            />
          </div>
        )}
      </div>

      {/* Document Detail Drawer */}
      <DocumentDetailDrawer
        open={!!selectedDocId}
        onClose={() => setSelectedDocId(null)}
        documentId={selectedDocId}
        event={selectedEvent}
      />
    </div>
  )
}
